import {formatDate} from '@angular/common';
import {inject, LOCALE_ID, Pipe, PipeTransform} from '@angular/core';

export type ItalianDateValue = string | number | Date | null | undefined;

export function formatItalianDate(
  value: ItalianDateValue,
  format: string,
  locale: string,
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (value instanceof Date || typeof value === 'number') {
    return safeFormat(value, format, locale);
  }

  const strVal = String(value).trim();
  const isDateOnly = isDateOnlyString(strVal);
  const effectiveFormat = isDateOnly ? stripTimeFromFormat(format) : format;

  const dateOnlyIsoMatch = strVal.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyIsoMatch) {
    const [, y, m, d] = dateOnlyIsoMatch;
    return safeFormat(new Date(Number(y), Number(m) - 1, Number(d)), effectiveFormat, locale);
  }

  const parsedItalian = parseItalianDateTime(strVal);
  if (parsedItalian) {
    return safeFormat(parsedItalian, effectiveFormat, locale);
  }

  const normalizedIso = normalizeIsoDateTime(strVal);
  const parsed = new Date(normalizedIso);
  if (!Number.isNaN(parsed.getTime())) {
    return safeFormat(parsed, effectiveFormat, locale);
  }

  return value;
}

function isDateOnlyString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{2}\/\d{2}\/\d{4}$/.test(value);
}

function stripTimeFromFormat(format: string): string {
  return format
    .replace(/\s*[-–—]\s*HH:mm(?::ss)?/i, '')
    .replace(/\s+HH:mm(?::ss)?/i, '')
    .replace(/HH:mm(?::ss)?/i, '')
    .trim();
}

function safeFormat(value: string | number | Date, format: string, locale: string): string {
  try {
    return formatDate(value, format, locale || 'it-IT');
  } catch {
    try {
      return formatDate(value, format, 'en-US');
    } catch {
      return `${value}`;
    }
  }
}

function parseItalianDateTime(value: string): Date | undefined {
  const fullMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (fullMatch) {
    const [, day, month, year, hours, minutes, seconds] = fullMatch;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      seconds ? Number(seconds) : 0,
      0,
    );
  }

  const dateOnlyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dateOnlyMatch) {
    const [, day, month, year] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
  }

  return undefined;
}

function normalizeIsoDateTime(value: string): string {
  return value.replace(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.(\d+))?((Z)|([+\-]\d{2}:?\d{2}))?$/,
    (_match, base: string, _fractional: string, digits: string, timezone: string) => {
      const ms = digits ? `.${digits.slice(0, 3).padEnd(3, '0')}` : '';
      return `${base}${ms}${timezone ?? ''}`;
    },
  );
}

@Pipe({
  name: 'italianDate',
  standalone: true,
})
export class ItalianDatePipe implements PipeTransform {
  private readonly locale = inject(LOCALE_ID);

  transform(value: ItalianDateValue, format = 'dd/MM/yyyy HH:mm'): string {
    return formatItalianDate(value, format, this.locale);
  }
}
